import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath, Paginated } from '../../../shared/api/types';
import { notifyOnError } from '../../../shared/api/onQueryError';
import type { CatalogEntryDto, CatalogFiltersDto } from '../model/dto';
import { mapEntry, mapFilters } from '../model/mappers';
import type { CatalogEntriesPage, CatalogEntriesParams, CatalogEntry, CatalogFilters } from '../model/types';
import type { DateRange } from '../../../components/ui/DatePicker';

const CATALOG = '/api/admin/catalog_products';

export type CatalogExportKind = 'products' | 'analogs' | 'all';

export const catalogExportPath = (kind: CatalogExportKind, range: DateRange | null): ApiPath => {
    const params = new URLSearchParams({ kind });

    if (range) {
        params.set('date_from', range.start);
        params.set('date_to', range.end);
    }

    return `${CATALOG}/export?${params}`;
};

function cleanParams(params: CatalogEntriesParams): Record<string, string | number> {
    return Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
    ) as Record<string, string | number>;
}

export const productAnalogsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getCatalogEntries: builder.query<CatalogEntriesPage, CatalogEntriesParams>({
            query: (params) => ({
                url: CATALOG satisfies ApiPath,
                params: cleanParams(params),
            }),
            transformResponse: (response: Paginated<CatalogEntryDto>) => ({
                items: (response.data ?? []).map(mapEntry),
                total: response.count ?? 0,
                pages: response.pages ?? 1,
            }),
            providesTags: ['CatalogEntries'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось загрузить товары и аналоги');
            },
        }),

        getCatalogEntryAnalogs: builder.query<CatalogEntry[], string>({
            query: (id) => ({ url: `${CATALOG}/${id}/analogs` satisfies ApiPath }),
            transformResponse: (response: { data?: CatalogEntryDto[] }) => (response.data ?? []).map(mapEntry),
            providesTags: (_result, _error, id) => [{ type: 'CatalogEntryAnalogs', id }],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось загрузить аналоги');
            },
        }),

        getCatalogEntry: builder.query<CatalogEntry, string>({
            query: (id) => ({ url: `${CATALOG}/${id}` satisfies ApiPath }),
            transformResponse: (response: CatalogEntryDto) => mapEntry(response),
            providesTags: (_result, _error, id) => [{ type: 'CatalogEntries', id }],
        }),

        getCatalogFilters: builder.query<CatalogFilters, void>({
            query: () => ({ url: `${CATALOG}/filters` satisfies ApiPath }),
            transformResponse: (response: CatalogFiltersDto) => mapFilters(response),
            providesTags: ['CatalogFilters'],
            onQueryStarted: (_arg, { queryFulfilled }) => {
                void notifyOnError(queryFulfilled, 'Не удалось загрузить фильтры');
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetCatalogEntriesQuery,
    useGetCatalogEntryAnalogsQuery,
    useGetCatalogEntryQuery,
    useGetCatalogFiltersQuery,
} = productAnalogsApi;